import { Box, ButtonBase, Tooltip } from "@mui/material";
import { Link as LinkIcon, Repeat } from "lucide-react";
import { TagChip } from "./TagChip";
import { transactionTypeLabels } from "../lib/labels";
import { CategoryIcon, categoryDisplayName, currencySymbol, money } from "../lib/format";
import type { Account, Category, Member, Transaction, TransactionLinkedLeg } from "../lib/types";

function transactionTitle(transaction: Transaction, category?: Category) {
  if (transaction.type === "transfer") return transaction.note || "Transfer";
  if (transaction.type === "correction") return transaction.note || "Balance correction";
  return transaction.merchant || transaction.note || category?.name || transactionTypeLabels[transaction.type] || transaction.type;
}

export function transactionAmountText(transaction: Transaction) {
  const amountValue = Number(transaction.amount || 0);
  const sign = transaction.type === "expense" || (transaction.type === "correction" && amountValue < 0) ? "-" : transaction.type === "income" || transaction.type === "correction" ? "+" : "";
  return `${sign}${currencySymbol(transaction.currency)}${money(transaction.type === "correction" ? Math.abs(amountValue) : transaction.amount)}`;
}

type TransactionRowProps = {
  transaction: Transaction;
  accounts: Account[];
  categories: Category[];
  members: Member[];
  onClick: () => void;
};

export function TransactionRow({ transaction, accounts, categories, members, onClick }: TransactionRowProps) {
  const account = accounts.find((item) => item.id === transaction.account_id);
  const transferAccount = accounts.find((item) => item.id === transaction.transfer_account_id);
  const category = categories.find((item) => item.id === transaction.category_id);
  const member = members.find((item) => item.user_id === transaction.user_id);
  const amountValue = Number(transaction.amount || 0);
  const amountClass = transaction.type === "expense" || (transaction.type === "correction" && amountValue < 0) ? "amount-expense" : transaction.type === "income" || transaction.type === "correction" ? "amount-income" : "amount-transfer";
  const sign = transaction.type === "expense" || (transaction.type === "correction" && amountValue < 0) ? "-" : transaction.type === "income" || transaction.type === "correction" ? "+" : "";
  const categoryName = transaction.type === "transfer" ? `${account?.name || "No account"} to ${transferAccount?.name || "No account"}` : transaction.type === "correction" ? "Balance correction" : category ? categoryDisplayName(category, categories) : "No category";

  return (
    <ButtonBase className={`record-row record-${transaction.type}${transaction.is_archived ? " archived" : ""}`} type="button" onClick={onClick}>
      <Box className="record-item-main">
        <CategoryIcon icon={transaction.type === "transfer" || transaction.type === "correction" ? account?.icon : category?.icon} color={transaction.type === "transfer" || transaction.type === "correction" ? account?.color : category?.color} />
        <Box className="record-copy">
          <span className="record-title-line">
            <strong>{transactionTitle(transaction, category)}</strong>
            {transaction.recurring_payment_id ? (
              <Tooltip title="Created from a recurring payment.">
                <span className="record-source-icon" aria-label="Recurring payment record"><Repeat size={14} /></span>
              </Tooltip>
            ) : null}
          </span>
          <span className="record-breadcrumbs">
            <span>{account?.name || "No account"} · {member?.display_name || "Unknown user"}</span>
            <span className={category?.is_archived ? "record-category archived" : "record-category"}>{categoryName}</span>
          </span>
        </Box>
      </Box>
      <Box className="record-row-side">
        <strong className={amountClass}>
          <span className="amount-sign">{sign}</span>
          <span className="amount-currency">{currencySymbol(transaction.currency)}</span>
          <span className="amount-value">{money(transaction.type === "correction" ? Math.abs(amountValue) : transaction.amount)}</span>
        </strong>
        {transaction.tags?.length ? <span className="record-row-tags">{transaction.tags.map((tag) => <TagChip key={tag.id} tag={tag} />)}</span> : null}
      </Box>
    </ButtonBase>
  );
}

function SyntheticTransactionRow({ leg, accounts, onOpen }: { leg: TransactionLinkedLeg; accounts: Account[]; onOpen?: () => void }) {
  const account = accounts.find((item) => item.id === leg.account_id);
  const outgoing = leg.type === "expense" || leg.type === "transfer";
  const content = (
    <>
      <Box className="record-item-main">
        <CategoryIcon icon={account?.icon} color={account?.color} />
        <Box className="record-copy">
          <span className="record-title-line"><strong>{leg.merchant || leg.note || "Transfer"}</strong></span>
          <span className="record-breadcrumbs"><span>{account?.name || "No account"}</span></span>
        </Box>
      </Box>
      <Box className="record-row-side">
        <strong className={outgoing ? "amount-expense" : "amount-income"}>
          <span className="amount-sign">{outgoing ? "-" : "+"}</span>
          <span className="amount-currency">{currencySymbol(leg.currency)}</span>
          <span className="amount-value">{money(leg.amount)}</span>
        </strong>
      </Box>
    </>
  );
  return onOpen ? <ButtonBase className="record-row" type="button" onClick={onOpen}>{content}</ButtonBase> : <Box className="record-row record-linked-leg-static">{content}</Box>;
}

type LinkedTransactionGroupProps = {
  primary: Transaction;
  partner: Transaction | TransactionLinkedLeg | null;
  accounts: Account[];
  categories: Category[];
  members: Member[];
  onOpen: (transaction: Transaction) => void;
  onOpenId: (id: string) => void;
};

export function LinkedTransactionGroup({ primary, partner, accounts, categories, members, onOpen, onOpenId }: LinkedTransactionGroupProps) {
  const outflowIsPrimary = primary.type !== "income";
  const renderLeg = (leg: Transaction | TransactionLinkedLeg | null) => {
    if (!leg) return null;
    return "is_archived" in leg
      ? <TransactionRow transaction={leg} accounts={accounts} categories={categories} members={members} onClick={() => onOpen(leg)} />
      : <SyntheticTransactionRow leg={leg} accounts={accounts} onOpen={() => onOpenId(leg.id)} />;
  };

  return (
    <Box className="record-linked-group">
      <Box className="record-linked-group-header"><LinkIcon size={13} aria-hidden="true" /><span>Linked transfer</span></Box>
      <Box className="record-linked-group-rows">
        {renderLeg(outflowIsPrimary ? primary : partner)}
        {renderLeg(outflowIsPrimary ? partner : primary)}
      </Box>
    </Box>
  );
}
